/**
 * The cut list (PLAN §3, step 8) — what the plan gave up, biggest sacrifice
 * first.
 *
 * `over-budget` cuts lead, because those are the ones you didn't choose: the
 * entry survived every filter you set and still didn't fit. The rest are your
 * own filters answering for themselves, which is useful but not a sacrifice.
 *
 * `whatYouMiss` is printed verbatim and never truncated. It is the honest part
 * of the feature — a cut list that hides the cost of its cuts is just a
 * shorter list.
 */

import { formatMinutes } from "@/components/entry/RuntimeChip.tsx";
import type { Cut, CutReason } from "@/lib/solver/index.ts";
import styles from "./plan.module.css";

const REASON_LABEL: Record<CutReason, string> = {
  "over-budget": "didn't fit",
  "tier-ceiling": "below your importance ceiling",
  "series-excluded": "series are off",
  "quality-floor": "under your quality floor",
  "runtime-cap": "over your longest-stop cap",
  "character-filter": "outside your character picks",
  "thread-filter": "outside your thread picks",
};

/** Only this one is the solver's doing; the rest are the user's own filters. */
const isSacrifice = (reason: CutReason) => reason === "over-budget";

export function CutList({ cuts }: { cuts: Cut[] }) {
  if (cuts.length === 0) return null;

  const sacrifices = cuts.filter((c) => isSacrifice(c.reason));
  const filtered = cuts.filter((c) => !isSacrifice(c.reason));

  return (
    <section className={styles.cuts} aria-labelledby="cut-list-title">
      <h3 className={styles.sectionTitle} id="cut-list-title">
        What it costs you
      </h3>

      {sacrifices.length > 0 ? (
        <>
          <p className={styles.cutsIntro}>
            {sacrifices.length} {sacrifices.length === 1 ? "stop" : "stops"} survived your filters
            and still didn't fit the time:{" "}
            {formatMinutes(sacrifices.reduce((n, c) => n + c.costMinutes, 0))} of viewing you're
            choosing not to do.
          </p>
          <ul className={styles.cutItems}>
            {sacrifices.map((cut) => (
              <li key={cut.entryId} className={styles.cut}>
                <div className={styles.cutHead}>
                  <span className={styles.cutTitle}>{cut.title}</span>
                  <span className={styles.cutCost}>{formatMinutes(cut.costMinutes)}</span>
                </div>
                <p className={styles.cutWhy}>{cut.whatYouMiss}</p>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className={styles.cutsIntro}>
          Nothing was cut for time. Everything that passed your filters fits.
        </p>
      )}

      {filtered.length > 0 && (
        <details className={styles.filteredOut}>
          <summary>{filtered.length} more ruled out by your filters, not by the clock</summary>
          <ul className={styles.cutItemsQuiet}>
            {filtered.map((cut) => (
              <li key={cut.entryId}>
                <span className={styles.cutTitle}>{cut.title}</span>{" "}
                <span className={styles.cutReason}>{REASON_LABEL[cut.reason]}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
